import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  ThumbsUp,
  MessageSquare,
  Share2,
  Bookmark,
  UserPlus,
  Image as ImageIcon,
  Video,
  FileText,
  Link as LinkIcon,
  Target,
  Send,
  Sparkles,
  AlertCircle,
  CheckCircle
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import { streamOpenAIResponse } from '../lib/openai';

interface QuizAnswers {
  platform: string;
  engagementType: string;
  contentType: string;
  includeStory: boolean;
  cta: string | null;
}

const EngagementQuiz = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<QuizAnswers>({
    platform: '',
    engagementType: '',
    contentType: '',
    includeStory: false,
    cta: null
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [generatedContent, setGeneratedContent] = useState<{
    ideas: string[];
    theme: string;
    goal: string;
  } | null>(null);

  const steps = [
    {
      title: "What platform are you posting on?",
      description: "Choose your primary social media platform",
      options: [
        { id: 'twitter', label: 'Twitter', icon: 'twitter' },
        { id: 'linkedin', label: 'LinkedIn', icon: 'linkedin' },
        { id: 'instagram', label: 'Instagram', icon: 'instagram' },
        { id: 'facebook', label: 'Facebook', icon: 'facebook' },
        { id: 'youtube', label: 'YouTube', icon: 'youtube' }
      ]
    },
    {
      title: "What type of engagement matters most?",
      description: "Select your primary engagement goal",
      options: [
        { id: 'likes', label: 'Likes', icon: ThumbsUp },
        { id: 'comments', label: 'Comments', icon: MessageSquare },
        { id: 'shares', label: 'Shares', icon: Share2 },
        { id: 'saves', label: 'Saves', icon: Bookmark },
        { id: 'follows', label: 'Follows', icon: UserPlus }
      ]
    },
    {
      title: "What kind of content do you want to post?",
      description: "Choose your content format",
      options: [
        { id: 'text', label: 'Text Only', icon: FileText },
        { id: 'image', label: 'Image', icon: ImageIcon },
        { id: 'video', label: 'Video', icon: Video },
        { id: 'link', label: 'Link', icon: LinkIcon }
      ]
    }
  ];

  const handleNext = async () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      await handleSubmit();
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => prev - 1);
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;
      if (!user) throw new Error('Not authenticated');

      // Save quiz answers
      const { data: quiz, error: quizError } = await supabase
        .from('campaign_quizzes')
        .insert({
          user_id: user.id,
          platform: answers.platform,
          engagement_type: answers.engagementType,
          content_type: answers.contentType,
          include_story: answers.includeStory,
          cta: answers.cta
        })
        .select()
        .single();

      if (quizError) throw quizError;

      // Generate content ideas using OpenAI
      let contentIdeas: string[] = [];
      let theme = '';
      let goal = '';

      const prompt = `Generate 3-5 engaging social media post ideas for ${answers.platform} that will drive ${answers.engagementType}. Content type: ${answers.contentType}. ${answers.includeStory ? 'Include a personal story element.' : ''} ${answers.cta ? `Call to action: ${answers.cta}` : ''}

Format your response as JSON with the following structure:
{
  "ideas": ["idea 1", "idea 2", "idea 3"],
  "theme": "overall content theme",
  "goal": "specific engagement goal"
}`;

      let responseText = '';

      await streamOpenAIResponse(
        [{ role: 'user', content: prompt }],
        (chunk) => {
          responseText += chunk;
          try {
            const data = JSON.parse(responseText);
            contentIdeas = data.ideas;
            theme = data.theme;
            goal = data.goal;
          } catch (e) {
            // Partial response, continue collecting
          }
        },
        (error) => {
          throw error;
        }
      );

      // Save generated results
      const { error: resultError } = await supabase
        .from('campaign_results')
        .insert({
          quiz_id: quiz.id,
          content_ideas: { ideas: contentIdeas },
          theme,
          goal
        });

      if (resultError) throw resultError;

      setGeneratedContent({ ideas: contentIdeas, theme, goal });

    } catch (err) {
      console.error('Error submitting quiz:', err);
      setError(err instanceof Error ? err.message : 'Failed to generate campaign');
    } finally {
      setLoading(false);
    }
  };

  const renderCurrentStep = () => {
    const step = steps[currentStep];
    if (!step) return null;

    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            {step.title}
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            {step.description}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {step.options.map((option) => {
            const Icon = option.icon;
            const isSelected = answers[
              currentStep === 0 ? 'platform' :
              currentStep === 1 ? 'engagementType' : 'contentType'
            ] === option.id;

            return (
              <motion.button
                key={option.id}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setAnswers(prev => ({
                  ...prev,
                  [currentStep === 0 ? 'platform' :
                   currentStep === 1 ? 'engagementType' : 'contentType']: option.id
                }))}
                className={`flex items-center p-4 rounded-lg border-2 transition-all ${
                  isSelected
                    ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20'
                    : 'border-gray-200 dark:border-dark-700 hover:border-indigo-300 dark:hover:border-indigo-700'
                }`}
              >
                <div className={`p-2 rounded-lg ${
                  isSelected
                    ? 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400'
                    : 'bg-gray-100 dark:bg-dark-700 text-gray-500 dark:text-gray-400'
                }`}>
                  <Icon className="h-5 w-5" />
                </div>
                <span className="ml-3 font-medium text-gray-900 dark:text-white">
                  {option.label}
                </span>
                {isSelected && (
                  <CheckCircle className="ml-auto h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                )}
              </motion.button>
            );
          })}
        </div>

        {currentStep === 2 && (
          <div className="space-y-4 mt-6">
            <div>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={answers.includeStory}
                  onChange={(e) => setAnswers(prev => ({
                    ...prev,
                    includeStory: e.target.checked
                  }))}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  Include a personal story or opinion
                </span>
              </label>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Call to Action (optional)
              </label>
              <input
                type="text"
                value={answers.cta || ''}
                onChange={(e) => setAnswers(prev => ({
                  ...prev,
                  cta: e.target.value
                }))}
                placeholder="What do you want people to do?"
                className="block w-full rounded-lg border-gray-300 dark:border-dark-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-dark-700 dark:text-white sm:text-sm"
              />
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderResults = () => {
    if (!generatedContent) return null;

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-8"
      >
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Your Engagement Campaign
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            Here are your AI-generated content ideas optimized for {answers.platform}
          </p>
        </div>

        <div className="bg-indigo-50 dark:bg-indigo-900/20 rounded-xl p-6">
          <div className="flex items-center space-x-3 mb-4">
            <Target className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
            <h3 className="text-lg font-semibold text-indigo-900 dark:text-indigo-100">
              Campaign Overview
            </h3>
          </div>
          <div className="space-y-3">
            <p className="text-indigo-800 dark:text-indigo-200">
              <strong>Theme:</strong> {generatedContent.theme}
            </p>
            <p className="text-indigo-800 dark:text-indigo-200">
              <strong>Goal:</strong> {generatedContent.goal}
            </p>
          </div>
        </div>

        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Content Ideas
          </h3>
          {generatedContent.ideas.map((idea, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white dark:bg-dark-800 rounded-xl p-6 border border-gray-100 dark:border-dark-700"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className="text-gray-900 dark:text-white">{idea}</p>
                  <div className="flex items-center space-x-4 mt-4">
                    <button className="text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300">
                      Edit
                    </button>
                    <button className="text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300">
                      Schedule
                    </button>
                  </div>
                </div>
                <div className="flex items-center space-x-2 ml-4">
                  <button className="p-2 text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-700">
                    <Share2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="flex justify-between pt-6 border-t border-gray-200 dark:border-dark-700">
          <button
            onClick={() => {
              setGeneratedContent(null);
              setCurrentStep(0);
              setAnswers({
                platform: '',
                engagementType: '',
                contentType: '',
                includeStory: false,
                cta: null
              });
            }}
            className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
          >
            Start Over
          </button>
          <div className="space-x-4">
            <button
              onClick={handleSubmit}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center"
            >
              <Sparkles className="h-4 w-4 mr-2" />
              Generate More
            </button>
            <button
              onClick={() => navigate('/calendar')}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center"
            >
              <Calendar className="h-4 w-4 mr-2" />
              Schedule All
            </button>
          </div>
        </div>
      </motion.div>
    );
  };

  return (
    <div className="max-w-4xl mx-auto">
      {error && (
        <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center space-x-2">
          <AlertCircle className="h-5 w-5 text-red-500 dark:text-red-400" />
          <p className="text-sm text-red-600 dark:text-red-300">{error}</p>
        </div>
      )}

      {loading ? (
        <div className="flex flex-col items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Generating your campaign...</p>
        </div>
      ) : generatedContent ? (
        renderResults()
      ) : (
        <div className="space-y-8">
          {/* Progress bar */}
          <div className="relative pt-1">
            <div className="flex mb-2 items-center justify-between">
              <div>
                <span className="text-xs font-semibold inline-block py-1 px-2 uppercase rounded-full text-indigo-600 bg-indigo-200 dark:bg-indigo-900/40 dark:text-indigo-400">
                  Progress
                </span>
              </div>
              <div className="text-right">
                <span className="text-xs font-semibold inline-block text-indigo-600 dark:text-indigo-400">
                  {Math.round((currentStep / (steps.length - 1)) * 100)}%
                </span>
              </div>
            </div>
            <div className="overflow-hidden h-2 mb-4 text-xs flex rounded bg-indigo-200 dark:bg-indigo-900/40">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${(currentStep / (steps.length - 1)) * 100}%` }}
                className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-indigo-600"
              />
            </div>
          </div>

          {renderCurrentStep()}

          <div className="flex justify-between pt-6">
            {currentStep > 0 ? (
              <button
                onClick={handleBack}
                className="flex items-center px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </button>
            ) : (
              <button
                onClick={() => navigate('/campaign-generator')}
                className="flex items-center px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Cancel
              </button>
            )}
            <button
              onClick={handleNext}
              disabled={!answers[
                currentStep === 0 ? 'platform' :
                currentStep === 1 ? 'engagementType' : 'contentType'
              ]}
              className={`flex items-center px-4 py-2 rounded-lg transition-colors ${
                answers[
                  currentStep === 0 ? 'platform' :
                  currentStep === 1 ? 'engagementType' : 'contentType'
                ]
                  ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                  : 'bg-gray-300 dark:bg-dark-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
              }`}
            >
              {currentStep === steps.length - 1 ? (
                <>
                  Generate
                  <Send className="h-4 w-4 ml-2" />
                </>
              ) : (
                <>
                  Next
                  <ArrowRight className="h-4 w-4 ml-2" />
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default EngagementQuiz;